import { useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTheme } from '../contexts/ThemeContext'
import { AuthService } from '../services/AuthService'
import { notifyError, notifySuccess } from '../utils/notify'
import { brand } from '@/ui/brand'

/** Mindestlänge für ein neues Passwort (Backend spiegelt dies). */
const MIN_PASSWORD_LENGTH = 8

/** Übersetzt einen fehlgeschlagenen Reset-Request in eine sprechende Meldung. */
function resetErrorMessage(error: unknown): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { status?: number } }).response?.status === 'number'
  ) {
    const status = (error as { response: { status: number } }).response.status
    if (status === 400) return 'Das Passwort erfüllt die Anforderungen nicht.'
    if (status === 401) return 'Der Link ist ungültig oder abgelaufen.'
  }
  return 'Etwas ist schiefgelaufen, bitte erneut versuchen.'
}

/**
 * Passwort-Reset-Seite (anonym, Route `/reset-password`). Liest `token` + `email`
 * aus der URL, validiert das neue Passwort (min. Länge + Gleichheit) und ruft
 * `AuthService.resetPassword`. Erfolg → Toast + Redirect auf `/login`.
 */
export function ResetPasswordPage() {
  const { theme } = useTheme()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const email = params.get('email') ?? ''
  const token = params.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const linkValid = Boolean(email && token)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setError('')

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Das Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`)
      return
    }
    // Reiner UI-Gleichheitscheck zweier Klartext-Eingaben (kein Secret-Vergleich).
    // eslint-disable-next-line security/detect-possible-timing-attacks
    if (password !== repeat) {
      setError('Die Passwörter stimmen nicht überein.')
      return
    }

    setSubmitting(true)
    AuthService.resetPassword(email, token, password)
      .then(() => {
        notifySuccess('Passwort gesetzt. Bitte melden Sie sich an.')
        navigate('/login', { replace: true })
      })
      .catch((err: unknown) => {
        const message = resetErrorMessage(err)
        setError(message)
        notifyError(message)
      })
      .finally(() => setSubmitting(false))
  }

  const logo = theme === 'dark' ? brand.logoDark : brand.logoLight

  return (
    <div className="login">
      <div className="login-card">
        <div className="lbrand">
          <img src={logo} alt={brand.name} className="brand-logo" />
        </div>
        <div className="eyebrow">Passwort zurücksetzen</div>
        <h1>
          {brand.name} <span className="text-primary">{brand.product}</span>
        </h1>

        {!linkValid && (
          <div className="loginform">
            <p className="text-sm text-muted-foreground">
              Dieser Link ist unvollständig oder ungültig. Fordern Sie den Reset bitte erneut an.
            </p>
            <button
              type="button"
              className="btn primary block"
              onClick={() => navigate('/login', { replace: true })}
            >
              Zurück zum Login
            </button>
          </div>
        )}

        {linkValid && (
          <form className="loginform" onSubmit={handleSubmit}>
            <p className="text-sm text-muted-foreground">Neues Passwort für {email} vergeben.</p>

            <div className="field">
              <label className="flabel" htmlFor="reset-password">
                Neues Passwort
              </label>
              <input
                className="inp"
                id="reset-password"
                type="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div className="field">
              <label className="flabel" htmlFor="reset-password-repeat">
                Passwort wiederholen
              </label>
              <input
                className="inp"
                id="reset-password-repeat"
                type="password"
                required
                autoComplete="new-password"
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
              />
            </div>

            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
            )}

            <button
              type="submit"
              className="btn primary block"
              disabled={submitting || !password || !repeat}
            >
              Passwort setzen
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
